import * as ko from 'knockout';

import { ListRequestParams } from '../api/request';
import { BaseLoadingScreen } from './base_loading_screen';
import { ListLoaderDelegate, delegateActions } from '../components/list_loader';
import { AgroRegion } from '../models/agro_region';
import * as agroRegionsApi from '../api/agro_regions';
import { countriesApi } from '../api/countries';
import { ListHeaderAction } from '../components/list_header';
import i18n from '../i18n';
import { translate } from '../i18n_text';
import { session } from '../session';
import {
  canCreateAgroRegion,
  canDeleteAgroRegion,
  canEditAgroRegion,
  reactListTablesEnabled,
} from '../permissions';
import { GridView } from '../utils/grid_view';
import { GridChoices, listLookup } from '../utils/grid_choices';
import {
  RegionsListChoices,
  RegionsListPage,
  RegionsListProps,
  RegionsListQuery,
  RegionsListRow,
} from 'react-islands';

const template = require('raw-loader!../../templates/agro_regions.html').default;

class AgroRegionsScreen
  extends BaseLoadingScreen
  implements ListLoaderDelegate<agroRegionsApi.AgroRegionData, AgroRegion>
{
  canDelete = canDeleteAgroRegion();
  listActions = ko.observableArray<ListHeaderAction>([]);

  newAgroRegionsTable = reactListTablesEnabled();

  private view = new GridView(
    'agroclimatic-regions',
    new GridChoices<RegionsListChoices>(
      {
        country: listLookup(countriesApi.list, 'country'),
      },
      () => ({
        country: [],
      })
    )
  );

  tableProps = ko.pureComputed<RegionsListProps>(() => ({
    view: this.view,
    fetchPage: this.fetchPage,
    exportFileName: 'agroclimatic-regions',
  }));

  constructor(params: { filters: unknown }) {
    super();

    const canCreate = canCreateAgroRegion();
    if (canCreate) {
      this.listActions.push({
        title: i18n.t('Add')(),
        icon: 'add_circle',
        href: '/agro_regions/new/',
        tooltipTitle: i18n.t('Add Agroclimatic Region')(),
      });
    }
    // Import bulk-creates and updates records, so it needs both add and change.
    if (canCreate && canEditAgroRegion()) {
      this.listActions.push({
        title: i18n.t('Import')(),
        icon: 'file_upload',
        href: '/agro_regions/import/',
      });
    }

    this.loadedAfter(this.newAgroRegionsTable ? this.view.load(params.filters) : Promise.resolve());
  }

  fetch(params: ListRequestParams) {
    return agroRegionsApi.list(params);
  }

  private fetchPage = (query: RegionsListQuery): Promise<RegionsListPage> =>
    this.view.fetchListPage(query, agroRegionsApi.list, 'name_prefix', this);

  toRow = (agroRegion: AgroRegion): RegionsListRow => ({
    id: agroRegion.id(),
    name: translate(agroRegion.nameJson()),
    url: session.toTenantPath(agroRegion.editUrl()),
    countryName: agroRegion.country()?.name || '',
    actions: delegateActions(this, agroRegion, this.view.reload),
  });

  instantiate = (agroRegionData: agroRegionsApi.AgroRegionData) => new AgroRegion(agroRegionData);

  remove(id: string) {
    return agroRegionsApi.remove(id);
  }

  canRemove(agroRegion: AgroRegion) {
    return this.canDelete;
  }
}

export const agroRegions = {
  name: 'agro-regions',
  viewModel: AgroRegionsScreen,
  template,
};

ko.components.register(agroRegions.name, agroRegions);
